import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "squiddi_admin_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

const USER_TOKEN_KEY = "squiddi_user_token";
export const getUserToken = () => localStorage.getItem(USER_TOKEN_KEY);
export const setUserToken = (t) => (t ? localStorage.setItem(USER_TOKEN_KEY, t) : localStorage.removeItem(USER_TOKEN_KEY));

const PRODUCTION_KEY = "squiddi_production_code";
export const getProductionCode = () => localStorage.getItem(PRODUCTION_KEY);
export const setProductionCode = (c) => (c ? localStorage.setItem(PRODUCTION_KEY, c) : localStorage.removeItem(PRODUCTION_KEY));

// Sessione area designer: token rilasciato dopo il PIN personale.
const DESIGNER_TOKEN_KEY = "squiddi_designer_token";
export const getDesignerToken = () => localStorage.getItem(DESIGNER_TOKEN_KEY);
export const setDesignerToken = (t) => (t ? localStorage.setItem(DESIGNER_TOKEN_KEY, t) : localStorage.removeItem(DESIGNER_TOKEN_KEY));

const client = axios.create({ baseURL: API });

// Attach the right bearer token: admin token for /admin routes, customer token otherwise.
client.interceptors.request.use((config) => {
  const url = config.url || "";
  if (url.startsWith("/production")) {
    const code = getProductionCode();
    if (code) config.headers["X-Production-Code"] = code;
    return config;
  }
  if (url.startsWith("/designer/")) {
    const dt = getDesignerToken();
    if (dt) config.headers["X-Designer-Token"] = dt;
  }
  const token = url.startsWith("/admin") ? getToken() : (getUserToken() || getToken());
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// --- Produzione (codice reparto) -----------------------------------------
export const productionListOrders = () => client.get("/production/orders").then((r) => r.data);
export const productionLoad = () => client.get("/production/load").then((r) => r.data);
export const productionOrderPdf = (orderId) => client.get(`/production/orders/${orderId}/scheda.pdf`, { responseType: "blob" }).then((r) => r.data);
export const productionOrderGraphics = (orderId) => client.get(`/production/orders/${orderId}/grafiche.zip`, { responseType: "blob" }).then((r) => r.data);
export const productionOrderLabel = (orderId) => client.get(`/production/orders/${orderId}/etichetta.pdf`, { responseType: "blob" }).then((r) => r.data);
export const productionOrderPack = (orderId) => client.get(`/production/orders/${orderId}/pacchetto.pdf`, { responseType: "blob" }).then((r) => r.data);
export const productionAllLabels = () => client.get("/production/etichette.pdf", { responseType: "blob" }).then((r) => r.data);
export const productionDayPack = () => client.get("/production/stampa-giorno.pdf", { responseType: "blob" }).then((r) => r.data);
// Impostazioni produzione: codice reparto + dati mittente dell'etichetta.
export const adminGetProductionSettings = () => client.get("/admin/production-settings").then((r) => r.data);
export const adminSaveProductionSettings = (payload) => client.put("/admin/production-settings", payload).then((r) => r.data);
// Pagina pubblica aperta dal QR sull'etichetta del pacco.
export const getPackage = (orderId) => client.get(`/pacco/${orderId}`).then((r) => r.data);
export const packagePdfUrl = (orderId) => `${API}/pacco/${orderId}.pdf`;
// Codice sconto 5% stampato sul PDF del pacco.
export const validateCoupon = (payload) => client.post("/coupons/validate", payload).then((r) => r.data);
// Spedizione: tariffa calcolata dal server (Spedisci.online, più economica + imballo).
export const shippingQuote = (payload) => client.post("/shipping/quote", payload).then((r) => r.data);
// Etichette corriere / ritiro / chiusura giornata (admin)
export const adminCreateLabel = (orderId, force = false) => client.post(`/admin/orders/${orderId}/label`, null, { params: force ? { force: true } : {} }).then((r) => r.data);
export const adminCancelLabel = (orderId) => client.delete(`/admin/orders/${orderId}/label`).then((r) => r.data);
export const adminGetLabelPdf = (orderId) => client.get(`/admin/orders/${orderId}/label.pdf`, { responseType: "blob" }).then((r) => r.data);
export const adminBookPickup = (payload) => client.post("/admin/shipping/pickup", payload).then((r) => r.data);
export const adminCloseDay = (payload = {}) => client.post("/admin/shipping/close-day", payload).then((r) => r.data);
export const adminShippingDiagnostics = () => client.get("/admin/shipping/diagnostics").then((r) => r.data);

// --- Public ---------------------------------------------------------------
export const uploadGraphic = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post("/uploads/graphic", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

// iter361 · Foto di riferimento per il designer (ispirazioni, non grafiche).
export const uploadReference = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post("/uploads/reference", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

// Ricamo/Ricamo 3D: salva la versione a 10 colori (per l'admin) + l'anteprima
// con trama filo (per l'ordine e "I miei impianti"). Riceve dataURL base64.
export const uploadEmbroidery = (simplified, preview) =>
  client.post("/uploads/embroidery", { simplified, preview }).then((r) => r.data);
export const getProducts = (params = {}) =>
  client.get("/products", { params }).then((r) => r.data);
export const searchProducts = (q, limit = 60) =>
  client.get("/search/products", { params: { q, limit } }).then((r) => r.data.results || []);
export const getProduct = (slug, preview = false) =>
  client.get(`/products/${slug}${preview ? "?preview=1" : ""}`).then((r) => r.data);
export const getCategories = () =>
  client.get("/categories").then((r) => r.data);
export const getCustomizerConfig = () =>
  client.get("/customizer/config").then((r) => r.data);
export const getProductTopTechniques = (slug) =>
  client.get(`/products/${slug}/top-techniques`).then((r) => r.data);
// Classifica globale delle lavorazioni: uguale su tutti i capi, cresce con gli acquisti.
export const getTopTechniques = () =>
  client.get("/techniques/top").then((r) => r.data); // { source, live, sample, items:[{id,label,pct}] }
// SQUIDDI-OPT: Abbinamenti reali "spesso acquistati insieme" - 2026-06-14
export const getFrequentlyBought = (slug, limit = 6) =>
  client.get(`/products/${slug}/frequently-bought`, { params: { limit } }).then((r) => r.data);
export const createQuote = (payload) =>
  client.post("/quotes", payload).then((r) => r.data);
// Numeri reali del catalogo per la schermata iniziale della chat IA.
export const aiMeta = () => client.get("/ai/meta").then((r) => r.data);

// --- Customer auth --------------------------------------------------------
export const authRegister = (payload) =>
  client.post("/auth/register", payload).then((r) => { setUserToken(r.data.token); return r.data; });
export const authLogin = (payload) =>
  client.post("/auth/login", payload).then((r) => { setUserToken(r.data.token); return r.data; });
// Carrello dimenticato: snapshot lato server per il promemoria a 24h.
export const saveCartSnapshot = (payload) =>
  client.post("/cart/snapshot", payload).then((r) => r.data);
export const clearCartSnapshot = (email) =>
  client.post("/cart/snapshot/clear", { email }).then((r) => r.data);
export const getCartRecovery = (id) =>
  client.get(`/cart/recovery/${id}`).then((r) => r.data);

// Login Google brandizzato: client_id pubblico del nostro progetto Google Cloud
// + scambio dell'authorization code lato server.
export const googleAuthConfig = () => client.get("/auth/google/config").then((r) => r.data);
export const authGoogleCode = (code, redirect_uri) =>
  client.post("/auth/google/code", { code, redirect_uri }).then((r) => { setUserToken(r.data.token); return r.data; });
export const authMe = () => client.get("/auth/me").then((r) => r.data);export const authLogout = () => setUserToken(null);
// Accesso con codice via email (OTP) + reset password.
export const requestLoginCode = (email) =>
  client.post("/auth/login-code/request", { email }).then((r) => r.data);
export const verifyLoginCode = (email, code) =>
  client.post("/auth/login-code/verify", { email, code }).then((r) => { setUserToken(r.data.token); return r.data; });
export const requestPasswordReset = (email) =>
  client.post("/auth/password-reset/request", { email }).then((r) => r.data);
export const confirmPasswordReset = (token, password) =>
  client.post("/auth/password-reset/confirm", { token, password }).then((r) => { setUserToken(r.data.token); return r.data; });
// Cascade-delete the current user account (users, orders, drafts, quotes,
// wecreate, impianti, library). Requires the user to re-type their email.
export const deleteMyAccount = (confirmEmail) => client.delete("/me", { data: { confirm_email: confirmEmail } }).then((r) => r.data);

// Ritocco prezzi in blocco: somma/sottrae un importo netto a tutti i prezzi.
export const adminPriceBump = (delta, productIds) => client.post("/admin/products/price-bump", { delta, product_ids: productIds || null }).then((r) => r.data);
export const adminPriceBumpLog = () => client.get("/admin/products/price-bump/log").then((r) => r.data);

// --- Design salvati sull'account ("I miei design") + autosalvataggio -------
export const listMyProjects = () => client.get("/me/projects").then((r) => r.data);
export const putMyProject = (id, payload) => client.put(`/me/projects/${id}`, { payload }).then((r) => r.data);
export const importMyProjects = (projects) => client.post("/me/projects/import", { projects }).then((r) => r.data);
export const renameMyProject = (id, name) => client.patch(`/me/projects/${id}`, { name }).then((r) => r.data);
export const deleteMyProject = (id) => client.delete(`/me/projects/${id}`).then((r) => r.data);
export const getMyAutosave = (slug) => client.get(`/me/customizer/autosave/${slug}`).then((r) => r.data);
export const putMyAutosave = (slug, payload) => client.put(`/me/customizer/autosave/${slug}`, { payload }).then((r) => r.data);
export const clearMyAutosave = (slug) => client.delete(`/me/customizer/autosave/${slug}`).then((r) => r.data);

// --- Orders (customer) ----------------------------------------------------
export const createOrder = (payload) => client.post("/orders", payload).then((r) => r.data);
// SQUIDDI-OPT: Acquisto senza registrazione - 2026-06-14
// L'ordine da ospite resta legato a questo browser tramite il suo token.
const GUEST_ORDER_KEY = "squiddi_guest_order";
export const saveGuestOrder = (orderId, guestToken) => {
  try { localStorage.setItem(GUEST_ORDER_KEY, JSON.stringify({ order_id: orderId, guest_token: guestToken })); } catch {}
};
export const getGuestOrder = () => {
  try { return JSON.parse(localStorage.getItem(GUEST_ORDER_KEY)) || null; } catch { return null; }
};
export const clearGuestOrder = () => { try { localStorage.removeItem(GUEST_ORDER_KEY); } catch {} };
// Conferma email dell'ospite al checkout: codice a 6 cifre → prova valida 2 ore.
export const requestCheckoutCode = (email) =>
  client.post("/auth/checkout-email/request", { email }).then((r) => r.data);
export const verifyCheckoutCode = (email, code) =>
  client.post("/auth/checkout-email/verify", { email, code }).then((r) => r.data);
const CHECKOUT_PROOF_KEY = "squiddi_checkout_email";
export const saveCheckoutProof = (proof) => {
  try { localStorage.setItem(CHECKOUT_PROOF_KEY, JSON.stringify(proof)); } catch {}
};
export const loadCheckoutProof = () => {
  try {
    const p = JSON.parse(localStorage.getItem(CHECKOUT_PROOF_KEY));
    return p && p.proof && p.exp > Date.now() ? p : null;
  } catch { return null; }
};
export const clearCheckoutProof = () => { try { localStorage.removeItem(CHECKOUT_PROOF_KEY); } catch {} };
export const requestGuestAccount = (orderId, guestToken) =>
  client.post("/auth/guest-account/request", { order_id: orderId, guest_token: guestToken }).then((r) => r.data);
// Pagamento Stripe: l'ordine si conferma solo quando il pagamento riesce.
export const createCheckout = (orderId, guestToken = null) =>
  client.post("/payments/checkout", {
    order_id: orderId, origin_url: window.location.origin,
    ...(guestToken ? { guest_token: guestToken } : {}),
  }).then((r) => r.data);
export const getPaymentStatus = (sessionId) =>
  client.get(`/payments/status/${sessionId}`).then((r) => r.data);
export const getMyOrders = () => client.get("/orders").then((r) => r.data);
// Segui l'ordine senza account: numero ordine + email dell'acquisto.
export const trackOrder = ({ order_id, email }) =>
  client.post("/orders/track", { order_id, email }).then((r) => r.data);
export const reorderPastOrder = (orderId) => client.post(`/orders/${orderId}/reorder`).then((r) => r.data);
// ─── Custom Garment Spec Sheet (Capo da Zero) ─────────────────────────
export const adminUpsertSpecSheet = (orderId, body) =>
  client.put(`/admin/orders/${orderId}/spec-sheet`, body).then((r) => r.data);
export const reorderCustomGarment = (orderId, body) =>
  client.post(`/me/orders/${orderId}/reorder-custom`, body).then((r) => r.data);

// ─── Shared Customizer Links (designer → cliente WhatsApp) ─────────────
export const createSharedLink = (body) => client.post("/admin/customizer-links", body).then((r) => r.data);
export const listSharedLinks = () => client.get("/admin/customizer-links").then((r) => r.data);
export const deleteSharedLink = (token) => client.delete(`/admin/customizer-links/${token}`).then((r) => r.data);
export const getSharedLink = (token) => client.get(`/customizer-links/${token}`).then((r) => r.data);
export const convertSharedLink = (token, action) =>
  client.post(`/customizer-links/${token}/convert`, { action }).then((r) => r.data);
// Customer-facing: share your own in-progress design (public, no auth).
export const createCustomerShareLink = (body) =>
  client.post("/customizer-links", body).then((r) => r.data);
// iter366 · parere di un collega sul link condiviso (pubblico, no auth).
export const sendDesignApproval = (token, body) =>
  client.post(`/customizer-links/${token}/approval`, body).then((r) => r.data);

// iter367 · Squiddi Cover: rimborso 100% sui progetti col designer.
export const getCoverOrders = () => client.get("/me/cover/orders").then((r) => r.data);
export const createCoverClaim = (body) => client.post("/me/cover/claims", body).then((r) => r.data);
export const getMyCoverClaims = () => client.get("/me/cover/claims").then((r) => r.data);
export const adminListCoverClaims = (status = "") =>
  client.get("/admin/cover/claims", { params: status ? { status } : {} }).then((r) => r.data);
export const adminPatchCoverClaim = (id, body) =>
  client.patch(`/admin/cover/claims/${id}`, body).then((r) => r.data);

export const getMyImpianti = () => client.get("/me/impianti").then((r) => r.data);
export const getMyImpiantiSavings = () => client.get("/me/impianti/savings").then((r) => r.data);
export const deleteMyImpianto = (id) => client.delete(`/me/impianti/${id}`).then((r) => r.data);
export const renameMyImpianto = (id, nickname) => client.patch(`/me/impianti/${id}`, { nickname }).then((r) => r.data);
export const matchMyImpianti = (items) => client.post("/me/impianti/match", { items }).then((r) => r.data);

// --- Bozze / drafts ---------------------------------------------------------
export const getMyDrafts = () => client.get("/me/drafts").then((r) => r.data);
export const toggleDraftFavorite = (id, favorite, extra = {}) =>
  client.post(`/me/drafts/${id}/favorite`, { favorite, ...extra }).then((r) => r.data);
export const respondDraft = (id, payload) => client.post(`/me/drafts/${id}/respond`, payload).then((r) => r.data);
export const adminGetDrafts = () => client.get("/admin/drafts").then((r) => r.data);
export const adminCreateDraft = (payload) => client.post("/admin/drafts", payload).then((r) => r.data);
export const adminUpdateDraft = (id, payload) => client.patch(`/admin/drafts/${id}`, payload).then((r) => r.data);
export const adminDeleteDraft = (id) => client.delete(`/admin/drafts/${id}`).then((r) => r.data);
// Preventivo → Bozza (copia l'intero design) e Bozza → Ordine (vendita chiusa fuori dal sito).
export const adminQuoteToDraft = (quoteId, payload) => client.post(`/admin/quotes/${quoteId}/to-draft`, payload).then((r) => r.data);
export const adminDraftToOrder = (draftId, payload) => client.post(`/admin/drafts/${draftId}/to-order`, payload).then((r) => r.data);

// --- Squiddi Designer customer directory -------------------------------------------
export const adminListWeCreateCustomers = () => client.get("/admin/wecreate/customers").then((r) => r.data);

// --- Designers (team) ------------------------------------------------------
export const adminListDesigners = () => client.get("/admin/designers").then((r) => r.data);
// Classifica designer del mese (lavori presi, chiusi, valore portato).
export const adminDesignersStats = (month = "") => client.get("/admin/designers/stats", { params: month ? { month } : {} }).then((r) => r.data);
export const designerGetStats = (designerId, month = "") => client.get(`/designer/${designerId}/stats`, { params: month ? { month } : {} }).then((r) => r.data);
// Auto-normalizzazione nomi colore (alias fornitore → nome ufficiale Squiddi).
export const adminColorAliases = () => client.get("/admin/colors/aliases").then((r) => r.data);
export const adminUpsertColorAlias = (source, target) => client.post("/admin/colors/aliases", { source, target }).then((r) => r.data);
export const adminDeleteColorAlias = (source) => client.delete("/admin/colors/aliases", { params: { source } }).then((r) => r.data);
export const adminLearnColorAliases = () => client.post("/admin/colors/aliases/learn").then((r) => r.data);
export const adminApproveColorAliases = (items, ignore = []) => client.post("/admin/colors/aliases/approve", { items, ignore }).then((r) => r.data);
export const adminSuggestColorName = (name, hex = "") => client.get("/admin/colors/suggest", { params: { name, hex } }).then((r) => r.data);
export const adminApplyColorNormalization = (dryRun = true, prunePalette = true) => client.post("/admin/colors/apply-catalog", { dry_run: dryRun, prune_palette: prunePalette }).then((r) => r.data);
export const adminCreateDesigner = (payload) => client.post("/admin/designers", payload).then((r) => r.data);
export const adminUpdateDesigner = (id, payload) => client.patch(`/admin/designers/${id}`, payload).then((r) => r.data);
export const adminDeleteDesigner = (id) => client.delete(`/admin/designers/${id}`).then((r) => r.data);
export const adminGetDesignerWorkload = (id) => client.get(`/admin/designers/${id}/workload`).then((r) => r.data);
export const adminSetDesignerPin = (id, pin) => client.post(`/admin/designers/${id}/pin`, { pin }).then((r) => r.data);
export const adminResetDesignerPin = (id) => client.post(`/admin/designers/${id}/pin/reset`).then((r) => r.data);
// Bacheca dei lavori liberi: nessuna assegnazione automatica, il designer
// clicca "Prendi in carico".
export const designerGetBoard = (designerId) => client.get(`/designer/${designerId}/board`).then((r) => r.data);
export const designerClaimWeCreateProject = (designerId, projectId) => client.post(`/designer/${designerId}/wecreate/projects/${projectId}/claim`).then((r) => r.data);
export const designerClaimQuote = (designerId, quoteId) => client.post(`/designer/${designerId}/quotes/${quoteId}/claim`).then((r) => r.data);
export const adminUnassignWeCreateProject = (id) => client.post(`/admin/wecreate/projects/${id}/unassign`).then((r) => r.data);
export const adminUnassignQuote = (id) => client.post(`/admin/quotes/${id}/unassign`).then((r) => r.data);
export const adminOverview = () => client.get("/admin/overview").then((r) => r.data);
export const listPublicDesigners = () => client.get("/designers/public").then((r) => r.data);
// NOTE: `registerDesigner` è stato rimosso in iter127 — la self-registration
// non è più supportata. I designer vengono creati dall'admin via CMS.
export const getDesignerDashboard = (designerId) => client.get(`/designer/${designerId}/dashboard`).then((r) => r.data);
// PIN personale del designer (primo accesso = set, poi verify).
export const designerPinStatus = (designerId) => client.get(`/designer/${designerId}/pin-status`).then((r) => r.data);
export const designerSetPin = (designerId, pin) => client.post(`/designer/${designerId}/pin/set`, { pin }).then((r) => r.data);
export const designerVerifyPin = (designerId, pin) => client.post(`/designer/${designerId}/pin/verify`, { pin }).then((r) => r.data);
export const designerGetDraftMessages = (designerId, draftId) => client.get(`/designer/${designerId}/drafts/${draftId}/messages`).then((r) => r.data);
export const designerPostDraftMessage = (designerId, draftId, payload) => client.post(`/designer/${designerId}/drafts/${draftId}/messages`, payload).then((r) => r.data);
export const designerPatchDraft = (designerId, draftId, payload) => client.patch(`/designer/${designerId}/drafts/${draftId}`, payload).then((r) => r.data);
export const designerGetNotifications = (designerId) => client.get(`/designer/${designerId}/notifications`).then((r) => r.data);
export const designerMarkNotificationsRead = (designerId, ids = []) => client.post(`/designer/${designerId}/notifications/read`, { ids }).then((r) => r.data);
export const designerListWeCreateProjects = (designerId) => client.get(`/designer/${designerId}/wecreate/projects`).then((r) => r.data);
export const designerUpdateProfile = (designerId, payload) => client.patch(`/designer/${designerId}/profile`, payload).then((r) => r.data);
export const designerCreateWeCreateSheet = (designerId, projectId, payload) => client.post(`/designer/${designerId}/wecreate/projects/${projectId}/sheets`, payload).then((r) => r.data);
export const designerUpdateWeCreateSheet = (designerId, projectId, sheetId, payload) => client.patch(`/designer/${designerId}/wecreate/projects/${projectId}/sheets/${sheetId}`, payload).then((r) => r.data);
export const designerDeleteWeCreateSheet = (designerId, projectId, sheetId) => client.delete(`/designer/${designerId}/wecreate/projects/${projectId}/sheets/${sheetId}`).then((r) => r.data);
export const designerRefundWeCreate = (designerId, projectId, reason) => client.post(`/designer/${designerId}/wecreate/projects/${projectId}/refund`, { reason }).then((r) => r.data);

// --- Draft chat + client selections + revisions ---------------------------
export const getMyDraftMessages = (id) => client.get(`/me/drafts/${id}/messages`).then((r) => r.data);
export const postMyDraftMessage = (id, msg) => client.post(`/me/drafts/${id}/messages`, typeof msg === "string" ? { text: msg } : msg).then((r) => r.data);
export const adminGetDraftMessages = (id) => client.get(`/admin/drafts/${id}/messages`).then((r) => r.data);
export const adminPostDraftMessage = (id, msg) => client.post(`/admin/drafts/${id}/messages`, typeof msg === "string" ? { text: msg } : msg).then((r) => r.data);
export const postMyDraftProposal = (id, payload) => client.post(`/me/drafts/${id}/proposal`, payload).then((r) => r.data);
export const setMyDraftSelections = (id, payload) => client.post(`/me/drafts/${id}/selections`, payload).then((r) => r.data);
export const getMyDraftsUnread = () => client.get("/me/drafts/unread-count").then((r) => r.data);

// --- Squiddi Designer v2 (pacchetto unico €49,90) ----------------------------------
export const getWeCreatePackageConfig = () => client.get("/wecreate/package/config").then((r) => r.data);
export const purchaseWeCreatePackage = (payload) => client.post("/wecreate/purchase", payload).then((r) => r.data);
export const listMyWeCreateProjects = () => client.get("/me/wecreate/projects").then((r) => r.data);
export const wecreateOnboarding = (projectId, payload) => client.post(`/wecreate/projects/${projectId}/onboarding`, payload).then((r) => r.data);
export const getWeCreateFaldone = (projectId) => client.get(`/wecreate/projects/${projectId}/faldone`).then((r) => r.data);
export const approveWeCreateSheet = (projectId, sheetId, approved = true) => client.post(`/wecreate/projects/${projectId}/sheets/${sheetId}/approve`, { approved }).then((r) => r.data);
export const confirmWeCreateFaldone = (projectId) => client.post(`/wecreate/projects/${projectId}/confirm`, {}).then((r) => r.data);
export const updateWeCreatePhone = (projectId, phone) => client.patch(`/wecreate/projects/${projectId}/phone`, { phone }).then((r) => r.data);
export const markWeCreateFaldoneSeen = (projectId) => client.post(`/wecreate/projects/${projectId}/seen`, {}).then((r) => r.data);
export const downloadFaldonePdf = (projectId) => client.get(`/wecreate/projects/${projectId}/faldone.pdf`, { responseType: "blob" }).then((r) => r.data);
export const adminListWeCreateProjects = () => client.get("/admin/wecreate/projects").then((r) => r.data);
export const adminUpdateWeCreateProject = (id, payload) => client.patch(`/admin/wecreate/projects/${id}`, payload).then((r) => r.data);
export const adminRefundWeCreateProject = (id, reason) => client.post(`/admin/wecreate/projects/${id}/refund`, { reason }).then((r) => r.data);
export const adminCreateWeCreateSheet = (id, payload) => client.post(`/admin/wecreate/projects/${id}/sheets`, payload).then((r) => r.data);
export const adminUpdateWeCreateSheet = (id, sheetId, payload) => client.patch(`/admin/wecreate/projects/${id}/sheets/${sheetId}`, payload).then((r) => r.data);
export const adminDeleteWeCreateSheet = (id, sheetId) => client.delete(`/admin/wecreate/projects/${id}/sheets/${sheetId}`).then((r) => r.data);
export const adminGetCustomerHistory = (email) => client.get(`/admin/customer-history`, { params: { email } }).then((r) => r.data);
export const designerGetCustomerHistory = (designerId, email) => client.get(`/designer/${designerId}/customer-history`, { params: { email } }).then((r) => r.data);
export const designerGetCustomerImpianti = (designerId, email) => client.get(`/designer/${designerId}/customer-impianti`, { params: { email } }).then((r) => r.data);
export const adminGetCustomerImpianti = (email) => client.get("/admin/customer-impianti", { params: { email } }).then((r) => r.data);

// Download all graphics attached to a quote as a single zip.
export const adminGetQuoteGraphicsZip = (quoteId) =>
  client.get(`/admin/quotes/${quoteId}/graphics.zip`, { responseType: "blob" }).then((r) => r.data);
export const adminReviewQuote = (id, payload) => client.patch(`/admin/quotes/${id}/review`, payload).then((r) => r.data);
export const adminRefundQuote = (id, reason) => client.post(`/admin/quotes/${id}/refund`, { reason }).then((r) => r.data);
export const adminAssignQuote = (id, designer_id) => client.patch(`/admin/quotes/${id}/assign`, { designer_id }).then((r) => r.data);
export const adminGetQuoteMessages = (id) => client.get(`/admin/quotes/${id}/messages`).then((r) => r.data);
export const adminPostQuoteMessage = (id, payload) => client.post(`/admin/quotes/${id}/messages`, payload).then((r) => r.data);
export const getMyQuoteMessages = (id) => client.get(`/me/quotes/${id}/messages`).then((r) => r.data);
export const postMyQuoteMessage = (id, payload) => client.post(`/me/quotes/${id}/messages`, payload).then((r) => r.data);
export const getMyQuotes = () => client.get("/me/quotes").then((r) => r.data);
export const designerListQuotes = (designerId) => client.get(`/designer/${designerId}/quotes`).then((r) => r.data);
export const designerGetQuoteMessages = (designerId, id) => client.get(`/designer/${designerId}/quotes/${id}/messages`).then((r) => r.data);
export const designerPostQuoteMessage = (designerId, id, payload) => client.post(`/designer/${designerId}/quotes/${id}/messages`, payload).then((r) => r.data);

// --- Account (customer) ---------------------------------------------------
export const updateProfile = (data) => client.put("/me/profile", data).then((r) => r.data);
export const getAddresses = () => client.get("/me/addresses").then((r) => r.data);
export const addAddress = (data) => client.post("/me/addresses", data).then((r) => r.data);
export const updateAddress = (id, data) => client.put(`/me/addresses/${id}`, data).then((r) => r.data);
export const deleteAddress = (id) => client.delete(`/me/addresses/${id}`).then((r) => r.data);
export const getFavorites = () => client.get("/me/favorites").then((r) => r.data);
export const toggleFavorite = (slug) => client.post("/me/favorites", { slug }).then((r) => r.data);
export const removeFavorite = (slug) => client.delete(`/me/favorites/${slug}`).then((r) => r.data);

// --- Orders (admin) -------------------------------------------------------
export const adminGetOrders = () => client.get("/admin/orders").then((r) => r.data);
// Lista completa del catalogo con le referenze fornitore (Excel / PDF).
export const adminCatalogSuppliers = () => client.get("/admin/catalog/suppliers").then((r) => r.data);
export const adminCatalogExportXlsx = (brand = "") => client.get("/admin/catalog/export.xlsx", { params: brand ? { brand } : {}, responseType: "blob" }).then((r) => r.data);
export const adminCatalogExportPdf = (brand = "") => client.get("/admin/catalog/export.pdf", { params: brand ? { brand } : {}, responseType: "blob" }).then((r) => r.data);
export const adminCatalogListinoPdf = (params = {}) => client.get("/admin/catalog/listino.pdf", { params: typeof params === "string" ? (params ? { category: params } : {}) : params, responseType: "blob" }).then((r) => r.data);
export const adminListinoClients = () => client.get("/admin/catalog/listino-clients").then((r) => r.data);
export const adminListinoClientCreate = (payload) => client.post("/admin/catalog/listino-clients", payload).then((r) => r.data);
export const adminListinoClientUpdate = (id, payload) => client.patch(`/admin/catalog/listino-clients/${id}`, payload).then((r) => r.data);
export const adminListinoClientDelete = (id) => client.delete(`/admin/catalog/listino-clients/${id}`).then((r) => r.data);
// Catalogo B2B impaginato: generazione in background (documento pesante).
// Catalogo sfogliabile sul web (pubblico): /catalogo e /catalogo/<token>.
const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const catalogWeb = (token = "") =>
  client.get(token ? `/catalog/web/${token}` : "/catalog/web").then((r) => r.data);
export const catalogWebPdfUrl = (token = "") =>
  token ? `${API_ROOT}/catalog/web/${token}/listino.pdf` : `${API_ROOT}/catalog/web/listino.pdf`;
export const adminCatalogShares = () => client.get("/admin/catalog/share").then((r) => r.data);
export const adminCatalogShareCreate = (payload) => client.post("/admin/catalog/share", payload).then((r) => r.data);
export const adminCatalogSharePatch = (token, active) => client.patch(`/admin/catalog/share/${token}`, { active }).then((r) => r.data);
export const adminCatalogShareDelete = (token) => client.delete(`/admin/catalog/share/${token}`).then((r) => r.data);
export const adminCatalogB2bStart = (params = {}) => client.post("/admin/catalog/b2b", params).then((r) => r.data);
export const adminCatalogB2bStatus = (jobId) => client.get(`/admin/catalog/b2b/${jobId}`).then((r) => r.data);
export const adminCatalogB2bLatest = () => client.get("/admin/catalog/b2b/latest").then((r) => r.data);
export const adminCatalogB2bFile = (jobId) => client.get(`/admin/catalog/b2b/${jobId}/file`, { responseType: "blob", timeout: 180000 }).then((r) => r.data);
export const adminGetOrderPdf = (orderId) => client.get(`/admin/orders/${orderId}/pdf`, { responseType: "blob" }).then((r) => r.data);
export const adminGetOrderRecapPdf = (orderId) => client.get(`/admin/orders/${orderId}/recap.pdf`, { responseType: "blob" }).then((r) => r.data);
export const adminCreateManualOrder = (payload) => client.post(`/admin/orders/manual`, payload).then((r) => r.data);
export const adminUpdateManualOrder = (orderId, payload) => client.put(`/admin/orders/${orderId}/manual`, payload).then((r) => r.data);
export const adminSearchOrders = (params) => client.get(`/admin/orders/search`, { params }).then((r) => r.data);
export const adminGetOrdersBatchPdf = (scope) => client.get(`/admin/orders/batch-pdf`, { params: { scope }, responseType: "blob" }).then((r) => r.data);
export const adminGetSupplierListPdf = (scope = "to_order", byBrand = false, brand = "") => client.get(`/admin/orders/supplier-list-pdf`, { params: { scope, by_brand: byBrand, ...(brand ? { brand } : {}) }, responseType: "blob" }).then((r) => r.data);
export const adminGetOrderGraphicsZip = (orderId) => client.get(`/admin/orders/${orderId}/graphics.zip`, { responseType: "blob" }).then((r) => r.data);
export const adminGetOrderGraphic = (orderId, itemIdx, persIdx, variant) => client.get(`/admin/orders/${orderId}/graphics/${itemIdx}/${persIdx}`, { params: variant ? { variant } : {}, responseType: "blob" }).then((r) => r.data);
export const adminUpdateOrder = (id, payload) => client.patch(`/admin/orders/${id}`, payload).then((r) => r.data);
export const adminGetAlerts = () => client.get("/admin/alerts").then((r) => r.data);
export const adminResendOrderConfirmation = (orderId) => client.post(`/admin/orders/${orderId}/resend-confirmation`).then((r) => r.data);

// --- Admin email templates ------------------------------------------------
export const adminGetEmailTemplates = () => client.get("/admin/email-templates").then((r) => r.data);
export const adminUpdateEmailTemplate = (key, data) => client.put(`/admin/email-templates/${key}`, data).then((r) => r.data);
// Stato SMTP + controllo DNS (SPF/DKIM/DMARC/MX) e invio di prova.
export const adminEmailStatus = () => client.get("/admin/email/status").then((r) => r.data);
export const adminSendTestEmail = (to) => client.post("/admin/email/test", { to }).then((r) => r.data);
// Registro email, automazioni e casella di posta admin.
export const adminEmailLog = (params = {}) => client.get("/admin/email/log", { params }).then((r) => r.data);
export const adminEmailLogDetail = (id) => client.get(`/admin/email/log/${id}`).then((r) => r.data);
export const adminEmailResend = (id) => client.post(`/admin/email/log/${id}/resend`).then((r) => r.data);
export const adminEmailAutomations = () => client.get("/admin/email/automations").then((r) => r.data);
export const adminToggleAutomation = (kind, enabled) => client.put(`/admin/email/automations/${kind}`, { enabled }).then((r) => r.data);
export const adminMailbox = (params = {}) => client.get("/admin/mailbox", { params }).then((r) => r.data);
export const adminMailboxCodes = (params = {}) => client.get("/admin/mailbox/codes", { params }).then((r) => r.data);
export const adminMailboxMessage = (uid) => client.get(`/admin/mailbox/${uid}`).then((r) => r.data);
export const adminMailboxReply = (uid, body) => client.post(`/admin/mailbox/${uid}/reply`, { body }).then((r) => r.data);
export const adminMailboxSend = ({ to, subject, body }) => client.post("/admin/mailbox/send", { to, subject, body }).then((r) => r.data);

// --- Newsletter -----------------------------------------------------------
export const newsletterSubscribe = (email, name = "") => client.post("/newsletter/subscribe", { email, name }).then((r) => r.data);
export const adminGetSubscribers = () => client.get("/admin/newsletter/subscribers").then((r) => r.data);
export const adminDeleteSubscriber = (id) => client.delete(`/admin/newsletter/subscribers/${id}`).then((r) => r.data);
export const adminGetCampaigns = () => client.get("/admin/newsletter/campaigns").then((r) => r.data);
export const adminSaveCampaign = (data) => client.post("/admin/newsletter/campaigns", data).then((r) => r.data);
export const adminDeleteCampaign = (id) => client.delete(`/admin/newsletter/campaigns/${id}`).then((r) => r.data);
export const adminSendCampaign = (id) => client.post(`/admin/newsletter/campaigns/${id}/send`).then((r) => r.data);

// --- Admin auth -----------------------------------------------------------
export const adminLogin = (email, password) =>
  client.post("/admin/login", { email, password }).then((r) => {
    setToken(r.data.token);
    return r.data;
  });
export const adminMe = () => client.get("/admin/me").then((r) => r.data);
// Accessi temporanei al pannello (collaboratori/agenzie)
export const adminListMembers = () => client.get("/admin/members").then((r) => r.data);
export const adminCreateMember = (payload) => client.post("/admin/members", payload).then((r) => r.data);
export const adminRevokeMember = (id) => client.delete(`/admin/members/${id}`).then((r) => r.data);
export const adminMeta = () => client.get("/admin/meta").then((r) => r.data);
export const adminAddPaletteColor = (name, hex, hex2 = "", extra_hexes = [], pattern = "") => client.post("/admin/palette", { name, hex, hex2, extra_hexes, pattern }).then((r) => r.data);
// iter250 · Upsert di N colori in UNA sola chiamata (niente race sulla palette).
export const adminAddPaletteColors = (colors) => client.post("/admin/palette/bulk", { colors }).then((r) => r.data);
export const adminDeletePaletteColor = (name) => client.delete("/admin/palette", { params: { name } }).then((r) => r.data);
// Modifica un colore della palette e la propaga a tutti i prodotti che lo usano.
export const adminEditPaletteColor = (payload) => client.post("/admin/colors/palette-edit", payload).then((r) => r.data);
// Quali colori della palette non sono usati da nessun prodotto (pulizia).
export const adminColorUsage = () => client.get("/admin/colors/usage").then((r) => r.data);
export const adminPruneUnusedColors = (names = []) => client.post("/admin/colors/prune-unused", { names }).then((r) => r.data);

// --- Admin uploads --------------------------------------------------------
export const uploadImage = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client
    .post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data); // { path, url }
};
export const adminGenerateFabricImage = (prompt) =>
  client.post("/admin/generate-fabric-image", { prompt }).then((r) => r.data); // { path, url }
// iter251 · Fascia a bolle via job ASYNC (anti-502 ingress su LLM lenti):
// POST /ai-generate-bubbles-async → {job_id} → poll /ai-extract-job/{id} ogni 2s.
// Stessa forma di ritorno { target, items }, così i chiamanti non cambiano.
export const adminGenerateBubbles = async (payload) => {
  const startRes = await client.post("/admin/products/ai-generate-bubbles-async", payload, { timeout: 30000 });
  const jobId = startRes.data && startRes.data.job_id;
  if (!jobId) throw new Error("Job non avviato");
  const started = Date.now();
  let pollErrors = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    await new Promise((r) => setTimeout(r, 2000));
    let s;
    try {
      s = await client.get(`/admin/products/ai-extract-job/${jobId}`, { timeout: 15000 });
    } catch (e) {
      // Tollera errori di rete transitori sul polling (max 4 di fila).
      if (++pollErrors > 4) throw e;
      continue;
    }
    pollErrors = 0;
    const st = s.data && s.data.status;
    if (st === "done") return s.data.result; // { target, items }
    if (st === "error" || st === "failed") {
      const err = new Error(s.data.error || "Generazione non riuscita");
      err.response = { status: s.data.code || 502, data: { detail: s.data.error || "Generazione non riuscita" } };
      throw err;
    }
    if (Date.now() - started > 120000) throw new Error("Timeout generazione (>2 min)");
  }
};

// --- AI Product Wizard ------------------------------------------------------
// iter193 · L'ingress k8s ha timeout ~60s. Fornitori con Cloudflare + molti
// colori superano la soglia, quindi usiamo il pattern job asincrono:
// POST /ai-extract-url-async → {job_id} → poll /ai-extract-job/{id} ogni 2s.
export const adminAiExtractUrl = async (url) => {
  const startRes = await client.post("/admin/products/ai-extract-url-async", { url }, { timeout: 30000 });
  const jobId = startRes.data && startRes.data.job_id;
  if (!jobId) throw new Error("Job non avviato");
  const started = Date.now();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    await new Promise((r) => setTimeout(r, 2000));
    const s = await client.get(`/admin/products/ai-extract-job/${jobId}`, { timeout: 15000 });
    const st = s.data && s.data.status;
    if (st === "done") return s.data.result;
    if (st === "failed") {
      const err = new Error(s.data.error || "Estrazione fallita");
      err.response = { status: s.data.code || 502, data: { detail: s.data.error || "Estrazione fallita" } };
      throw err;
    }
    if (Date.now() - started > 240000) throw new Error("Timeout estrazione (>4 min)");
  }
};
// iter235 · Import prezzi dal fornitore (login automatico) via job async.
// POST /import-supplier-prices-async → {job_id} → poll /ai-extract-job/{id}.
// Ritorna il risultato { ok, brand, kind, base, size_prices, message }.
export const adminImportSupplierPrices = async (url, sizes = [], colors = []) => {
  const startRes = await client.post("/admin/products/import-supplier-prices-async", { url, sizes, colors }, { timeout: 30000 });
  const jobId = startRes.data && startRes.data.job_id;
  if (!jobId) throw new Error("Job non avviato");
  const started = Date.now();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    await new Promise((r) => setTimeout(r, 2500));
    const s = await client.get(`/admin/products/ai-extract-job/${jobId}`, { timeout: 15000 });
    const st = s.data && s.data.status;
    if (st === "done") return s.data.result;
    if (st === "error" || st === "failed") {
      const err = new Error(s.data.error || "Import prezzi fallito");
      err.response = { status: s.data.code || 502, data: { detail: s.data.error || "Import prezzi fallito" } };
      throw err;
    }
    if (Date.now() - started > 180000) throw new Error("Timeout import prezzi (>3 min)");
  }
};
// iter238 · Import prezzi da SCREENSHOT (SOL'S/SILAN o qualsiasi fornitore).
// Gemini legge la tabella prezzi dall'immagine → netti ×3 per taglia/colore.
export const adminImportSupplierPricesImage = (file, sizes = [], colors = [], brand = "") => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("sizes", (sizes || []).join(","));
  fd.append("colors", (colors || []).join(","));
  fd.append("brand", brand || "");
  return client.post("/admin/products/import-supplier-prices-image", fd,
    { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 }).then((r) => r.data);
};
export const adminAiExtractFile = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post("/admin/products/ai-extract-file", fd,
    { headers: { "Content-Type": "multipart/form-data" }, timeout: 240000 }).then((r) => r.data);
};
// Combined extraction: link + N files (PDF/screenshot/photo). At least one input required.
// iter193 · Anch'esso passa attraverso il pattern job async per evitare timeout ingress.
export const adminAiExtractCombined = async ({ url = "", files = [] } = {}) => {
  const fd = new FormData();
  fd.append("url", url || "");
  (files || []).forEach((f) => fd.append("files", f));
  const startRes = await client.post("/admin/products/ai-extract-combined-async", fd,
    { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
  const jobId = startRes.data && startRes.data.job_id;
  if (!jobId) throw new Error("Job non avviato");
  const started = Date.now();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    await new Promise((r) => setTimeout(r, 2000));
    const s = await client.get(`/admin/products/ai-extract-job/${jobId}`, { timeout: 15000 });
    const st = s.data && s.data.status;
    if (st === "done") return s.data.result;
    if (st === "failed") {
      const err = new Error(s.data.error || "Estrazione fallita");
      err.response = { status: s.data.code || 502, data: { detail: s.data.error || "Estrazione fallita" } };
      throw err;
    }
    if (Date.now() - started > 300000) throw new Error("Timeout estrazione (>5 min)");
  }
};

// iter184 · adminSavePositionPreset rimosso: le 4 posizioni sono fisse.

// iter194 · Generazioni immagine via job async + polling (anti-timeout 502)
// con retry automatico lato server. Riusa lo status endpoint dei job extract.
// iter197 · Grace period sul 404: se il primo polling non trova il job (race
// col worker o mongo lento), ritenta fino a 5 volte prima di gettare.
// iter198 · onProgress({progress:0-100, log:[{t,msg}], status}) è chiamato ad
// ogni polling per alimentare la progress bar UI.
const pollAiJob = async (jobId, maxMs = 300000, onProgress) => {
  const started = Date.now();
  let notFoundStreak = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    await new Promise((r) => setTimeout(r, 2500));
    try {
      const s = await client.get(`/admin/products/ai-extract-job/${jobId}`, { timeout: 15000 });
      notFoundStreak = 0;
      const st = s.data && s.data.status;
      if (typeof onProgress === "function") {
        try { onProgress({ status: st, progress: Number(s.data.progress) || 0, log: s.data.log || [] }); }
        catch { /* ignore progress errors */ }
      }
      if (st === "done") return s.data.result;
      if (st === "failed") {
        const err = new Error(s.data.error || "Generazione AI fallita");
        err.response = { status: s.data.code || 502, data: { detail: s.data.error || "Generazione AI fallita" } };
        throw err;
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        notFoundStreak += 1;
        // iter197 · Grace period: tolleriamo fino a 5 letture consecutive 404
        // prima di segnalare l'errore. Copre reload backend + latenza Mongo.
        if (notFoundStreak < 5) continue;
      }
      throw e;
    }
    if (Date.now() - started > maxMs) throw new Error("Timeout generazione AI (>5 min)");
  }
};
const startAiImageJob = async (endpoint, payload, onProgress) => {
  const r = await client.post(endpoint, payload, { timeout: 30000 });
  if (!r.data || !r.data.job_id) throw new Error("Job non avviato");
  const out = await pollAiJob(r.data.job_id, 300000, onProgress);
  // SQUIDDI-gemini - 2026-10-09 · aggiorna la barra delle generazioni Gemini
  try { window.dispatchEvent(new CustomEvent("squiddi-gemini-quota-refresh")); } catch {}
  return out;
};
// SQUIDDI-gemini - 2026-10-09
export const adminGeminiQuota = () =>
  client.get("/admin/ai/gemini-quota").then((r) => r.data); // { used, limit, percent_used, level, warning, key_source }
export const adminAiGenerateProductImage = (payload, onProgress) =>
  startAiImageJob("/admin/products/ai-generate-image-async", payload, onProgress); // { path, url }
export const adminAiPreviewPrompt = (payload) =>
  client.post("/admin/products/ai-preview-prompt", payload, { timeout: 30000 }).then((r) => r.data); // { prompt, pose, references }
export const adminAiGenerateModel = (payload, onProgress) =>
  startAiImageJob("/admin/products/ai-generate-model-async", payload, onProgress); // { path, url, gender, description }
export const adminAiEditImage = (payload, onProgress) =>
  startAiImageJob("/admin/products/ai-edit-image-async", payload, onProgress); // { path, url }
export const adminAiGenerateBackground = (payload, onProgress) =>
  startAiImageJob("/admin/products/ai-generate-background-async", payload, onProgress); // { path, url, preset }
export const adminGetImageHistory = (params) =>
  client.get("/admin/products/image-history", { params }).then((r) => r.data); // { items: [...] }
// Studio immagini AI: mockup logo-su-capo + grafiche social/banner (Nano Banana).
export const adminAiStudioMockup = (payload, onProgress) =>
  startAiImageJob("/admin/ai-studio/mockup-async", payload, onProgress); // { id, url, kind, meta }
export const adminAiStudioSocial = (payload, onProgress) =>
  startAiImageJob("/admin/ai-studio/social-async", payload, onProgress); // { id, url, kind, meta }
export const adminAiStudioImages = (params = {}) =>
  client.get("/admin/ai-studio/images", { params }).then((r) => r.data); // { items, count }
export const adminAiStudioDeleteImage = (id) =>
  client.delete(`/admin/ai-studio/images/${id}`).then((r) => r.data);
export const adminAiStudioAttach = (id, payload) =>
  client.post(`/admin/ai-studio/images/${id}/attach`, payload).then((r) => r.data);
export const adminAiGenerateSilhouette = (payload, onProgress) =>
  startAiImageJob("/admin/products/ai-generate-silhouette-async", payload, onProgress); // { path, url, references_used }
// Modifica la sagoma scrivendo la correzione ("allunga le maniche").
export const adminAiEditSilhouette = (payload, onProgress) =>
  startAiImageJob("/admin/products/ai-edit-silhouette-async", payload, onProgress); // { path, url, view, instructions }
// iter203 · Colora tutte le sagome per_color LOCALMENTE (0 crediti AI):
// contorno perfetto (deriva della base) + tinta per colore.
export const adminSilhouetteTintBatchSave = (payload) =>
  client.post("/admin/products/silhouette-tint-batch-save", payload, { timeout: 60000 }).then((r) => r.data); // { count, view, colors, silhouette_color_mode, deleted_orphans }
// iter205 · Colora TUTTE le viste (fronte + lato + retro) in un colpo.
export const adminSilhouetteTintBatchSaveAllViews = (payload) =>
  client.post("/admin/products/silhouette-tint-batch-save-all-views", payload, { timeout: 120000 }).then((r) => r.data); // { total_count, views_processed, per_view, colors, deleted_orphans }

// Colorazione per colore in BACKGROUND (anti-timeout): un colore alla volta,
// con riprova, e ogni variante salvata subito. `onProgress` riceve {progress, log}.
export const adminSilhouetteTintBatchJob = async (payload, onProgress) => {
  const startRes = await client.post("/admin/products/silhouette-tint-batch-async", payload, { timeout: 30000 });
  const jobId = startRes.data && startRes.data.job_id;
  if (!jobId) throw new Error("Colorazione non avviata");
  const started = Date.now();
  let pollErrors = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    await new Promise((r) => setTimeout(r, 2000));
    let s;
    try {
      s = await client.get(`/admin/products/ai-extract-job/${jobId}`, { timeout: 15000 });
    } catch (e) {
      if (++pollErrors > 4) throw e;
      continue;
    }
    pollErrors = 0;
    const d = s.data || {};
    if (onProgress) onProgress({ progress: d.progress || 0, log: (d.log || []).slice(-1)[0] });
    if (d.status === "done") return d.result;
    if (d.status === "error" || d.status === "failed") {
      const err = new Error(d.error || "Colorazione non riuscita");
      err.response = { status: d.code || 502, data: { detail: d.error || "Colorazione non riuscita" } };
      throw err;
    }
    if (Date.now() - started > 900000) throw new Error("Colorazione troppo lunga (>15 min)");
  }
};
// iter209 · Tint di UN singolo colore con hex scelto dal color picker (0 crediti).
export const adminSilhouetteTint = (payload) =>
  client.post("/admin/products/silhouette-tint", payload, { timeout: 60000 }).then((r) => r.data); // { items:[{name,hex,path,url}], count, view }
// iter210 · Editor sagoma: ritaglia foro (cut) o secchiello colore fisso (fill).
export const adminSilhouetteEdit = (payload) =>
  client.post("/admin/products/silhouette-edit", payload, { timeout: 60000 }).then((r) => r.data); // { path, url, view, action }
// iter209 · Libreria foto tessuto già generate (riuso, 0 crediti).
export const adminGetFabricLibrary = () =>
  client.get("/admin/products/fabric-library").then((r) => r.data); // { items:[{path,url,prompt,created_at}], count }
// Elimina una foto tessuto dalla libreria (azzera il riferimento sui capi che la usano).
export const adminDeleteFabricImage = (path) =>
  client.post("/admin/products/fabric-library/delete", { path }).then((r) => r.data); // { ok, cleared_products }
// I 3 quadratini "In sintesi" (Vestibilità · Materiale · Tempo di produzione).
export const adminGenerateTiles = async (payload) => {
  const startRes = await client.post("/admin/products/ai-generate-tiles-async", payload, { timeout: 30000 });
  const jobId = startRes.data && startRes.data.job_id;
  if (!jobId) throw new Error("Job non avviato");
  const started = Date.now();
  let pollErrors = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    await new Promise((r) => setTimeout(r, 2000));
    let s;
    try {
      s = await client.get(`/admin/products/ai-extract-job/${jobId}`, { timeout: 15000 });
    } catch (e) {
      if (++pollErrors > 4) throw e;
      continue;
    }
    pollErrors = 0;
    const st = s.data && s.data.status;
    if (st === "done") return s.data.result; // { fit_headline, material_headline, production_time }
    if (st === "error" || st === "failed") throw new Error(s.data.error || "Generazione non riuscita");
    if (Date.now() - started > 120000) throw new Error("Timeout generazione (>2 min)");
  }
};

// iter252 · Testi tessuto (Titolo + Descrizione + Prompt foto) via job ASYNC.
export const adminGenerateFabricText = async (payload) => {
  const startRes = await client.post("/admin/products/ai-generate-fabric-async", payload, { timeout: 30000 });
  const jobId = startRes.data && startRes.data.job_id;
  if (!jobId) throw new Error("Job non avviato");
  const started = Date.now();
  let pollErrors = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    await new Promise((r) => setTimeout(r, 2000));
    let s;
    try {
      s = await client.get(`/admin/products/ai-extract-job/${jobId}`, { timeout: 15000 });
    } catch (e) {
      if (++pollErrors > 4) throw e;
      continue;
    }
    pollErrors = 0;
    const st = s.data && s.data.status;
    if (st === "done") return s.data.result; // { title, description, prompt }
    if (st === "error" || st === "failed") {
      const err = new Error(s.data.error || "Generazione non riuscita");
      err.response = { status: s.data.code || 502, data: { detail: s.data.error || "Generazione non riuscita" } };
      throw err;
    }
    if (Date.now() - started > 120000) throw new Error("Timeout generazione (>2 min)");
  }
};
// Descrizione commerciale generata dall'IA (tono artistico, "di nostra produzione").
export const adminGenerateDescription = async (payload) => {
  const startRes = await client.post("/admin/products/ai-generate-description-async", payload, { timeout: 30000 });
  const jobId = startRes.data && startRes.data.job_id;
  if (!jobId) throw new Error("Job non avviato");
  const started = Date.now();
  let pollErrors = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    await new Promise((r) => setTimeout(r, 2000));
    let s;
    try {
      s = await client.get(`/admin/products/ai-extract-job/${jobId}`, { timeout: 15000 });
    } catch (e) {
      if (++pollErrors > 4) throw e;
      continue;
    }
    pollErrors = 0;
    const st = s.data && s.data.status;
    if (st === "done") return s.data.result; // { description }
    if (st === "error" || st === "failed") {
      const err = new Error(s.data.error || "Generazione non riuscita");
      err.response = { status: s.data.code || 502, data: { detail: s.data.error || "Generazione non riuscita" } };
      throw err;
    }
    if (Date.now() - started > 120000) throw new Error("Timeout generazione (>2 min)");
  }
};
export const adminGenerateKeywords = async (payload) => {
  const startRes = await client.post("/admin/products/ai-generate-keywords-async", payload, { timeout: 30000 });
  const jobId = startRes.data && startRes.data.job_id;
  if (!jobId) throw new Error("Job non avviato");
  const started = Date.now();
  let pollErrors = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    await new Promise((r) => setTimeout(r, 2000));
    let s;
    try {
      s = await client.get(`/admin/products/ai-extract-job/${jobId}`, { timeout: 15000 });
    } catch (e) {
      if (++pollErrors > 4) throw e;
      continue;
    }
    pollErrors = 0;
    const st = s.data && s.data.status;
    if (st === "done") return s.data.result; // { keywords: [...] }
    if (st === "error" || st === "failed") {
      const err = new Error(s.data.error || "Generazione non riuscita");
      err.response = { status: s.data.code || 502, data: { detail: s.data.error || "Generazione non riuscita" } };
      throw err;
    }
    if (Date.now() - started > 120000) throw new Error("Timeout generazione (>2 min)");
  }
};
// Batch: genera keyword in blocco. onProgress({progress, status, msg}).
export const adminGenerateKeywordsBatch = async ({ overwrite = false } = {}, onProgress) => {
  const startRes = await client.post("/admin/products/ai-generate-keywords-batch", { overwrite }, { timeout: 30000 });
  const jobId = startRes.data && startRes.data.job_id;
  if (!jobId) throw new Error("Job non avviato");
  const started = Date.now();
  let pollErrors = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    await new Promise((r) => setTimeout(r, 2500));
    let s;
    try {
      s = await client.get(`/admin/products/ai-extract-job/${jobId}`, { timeout: 15000 });
    } catch (e) {
      if (++pollErrors > 5) throw e;
      continue;
    }
    pollErrors = 0;
    const d = s.data || {};
    const lastMsg = (d.log && d.log.length) ? d.log[d.log.length - 1].msg : "";
    onProgress && onProgress({ progress: d.progress || 0, status: d.status, msg: lastMsg });
    if (d.status === "done") return d.result; // { updated, total, processed, errors }
    if (d.status === "error" || d.status === "failed") {
      throw new Error(d.error || "Generazione non riuscita");
    }
    if (Date.now() - started > 900000) throw new Error("Timeout generazione in blocco (>15 min)");
  }
};
// Storie immersive delle tecniche (pagina /tecniche). onProgress({progress, status, msg}).
export const adminGenerateTechStories = async ({ overwrite = false, ids = [] } = {}, onProgress) => {
  const startRes = await client.post("/admin/customizer/ai-generate-stories", { overwrite, ids }, { timeout: 30000 });
  const jobId = startRes.data && startRes.data.job_id;
  if (!jobId) throw new Error("Job non avviato");
  const started = Date.now();
  let pollErrors = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    await new Promise((r) => setTimeout(r, 2500));
    let s;
    try {
      s = await client.get(`/admin/products/ai-extract-job/${jobId}`, { timeout: 15000 });
    } catch (e) {
      if (++pollErrors > 5) throw e;
      continue;
    }
    pollErrors = 0;
    const d = s.data || {};
    const lastMsg = (d.log && d.log.length) ? d.log[d.log.length - 1].msg : "";
    onProgress && onProgress({ progress: d.progress || 0, status: d.status, msg: lastMsg });
    if (d.status === "done") return d.result;
    if (d.status === "error" || d.status === "failed") throw new Error(d.error || "Generazione non riuscita");
    if (Date.now() - started > 900000) throw new Error("Timeout generazione storie (>15 min)");
  }
};
// Batch: rigenera le descrizioni in blocco. onProgress({progress, status, msg}).
export const adminGenerateDescriptionsBatch = async ({ overwrite = true, skipAiTone = true } = {}, onProgress) => {
  const startRes = await client.post("/admin/products/ai-generate-descriptions-batch", { overwrite, skip_ai_tone: skipAiTone }, { timeout: 30000 });
  const jobId = startRes.data && startRes.data.job_id;
  if (!jobId) throw new Error("Job non avviato");
  const started = Date.now();
  let pollErrors = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    await new Promise((r) => setTimeout(r, 2500));
    let s;
    try {
      s = await client.get(`/admin/products/ai-extract-job/${jobId}`, { timeout: 15000 });
    } catch (e) {
      if (++pollErrors > 5) throw e;
      continue;
    }
    pollErrors = 0;
    const d = s.data || {};
    const lastMsg = (d.log && d.log.length) ? d.log[d.log.length - 1].msg : "";
    onProgress && onProgress({ progress: d.progress || 0, status: d.status, msg: lastMsg });
    if (d.status === "done") return d.result; // { updated, total, processed, errors }
    if (d.status === "error" || d.status === "failed") {
      throw new Error(d.error || "Generazione non riuscita");
    }
    if (Date.now() - started > 1200000) throw new Error("Timeout generazione in blocco (>20 min)");
  }
};
export const adminUploadSilhouette = (file, view, productName) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("view", view || "front");
  fd.append("product_name", productName || "");
  return client.post("/admin/products/silhouette-upload", fd,
    { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 }).then((r) => r.data); // { path, url }
};
export const adminGetSilhouetteLibrary = () =>
  client.get("/admin/products/silhouette-library").then((r) => r.data); // { items:[{product_name, thumb, views:{front:{base,colors:[...]}...}}] }
// iter175 · Libreria cornici di stampa (print_masks)
export const adminGetFrameLibrary = () =>
  client.get("/admin/products/frame-library").then((r) => r.data); // { items:[{product_name, thumb, frames:{view:{points,silhouette,favorite}}}], presets:[{id,name,points,builtin}] }
export const adminToggleFrameFavorite = (product_id, view, favorite) =>
  client.post("/admin/products/frame-library/toggle-favorite", { product_id, view, favorite }).then((r) => r.data);
export const adminSaveFramePreset = (payload) =>
  client.post("/admin/products/frame-library/preset", payload).then((r) => r.data); // {id?, name, points}
export const adminDeleteFramePreset = (presetId) =>
  client.delete(`/admin/products/frame-library/preset/${presetId}`).then((r) => r.data);
// iter192 · Copia set completo di cornici da un prodotto all'altro
export const adminCopyFrameSet = ({ source_product_id, target_product_id, overwrite = true }) =>
  client.post("/admin/products/frame-library/copy-set", { source_product_id, target_product_id, overwrite }).then((r) => r.data);
// iter192 · Set cornici salvati come "preferiti" (bundle multi-vista)
export const adminSaveFrameSet = (payload) =>
  client.post("/admin/products/frame-library/set", payload).then((r) => r.data); // {name, source_product_id?} | {name, views:{view:{points}}}
export const adminDeleteFrameSet = (setId) =>
  client.delete(`/admin/products/frame-library/set/${setId}`).then((r) => r.data);
export const adminApplyFrameSet = (setId, { target_product_id, overwrite = true }) =>
  client.post(`/admin/products/frame-library/set/${setId}/apply`, { target_product_id, overwrite }).then((r) => r.data);
export const adminGetSilhouetteStatus = () =>
  client.get("/admin/products/silhouette-status").then((r) => r.data); // { items:[{product_id, name, views:{front:{sil,frame,colors,...}}, ready, missing}] }
export const adminDeleteSilhouettes = (data) =>
  client.post("/admin/products/silhouette-delete", data).then((r) => r.data); // { items:[{product_id, views:[...]}], include_colors, wipe_frames }
export const adminGetCustomizerDefaults = () =>
  client.get("/admin/customizer/defaults").then((r) => r.data);
// iter167 · generazione in serie delle sagome
export const adminSilhouetteBatchEstimate = (data) =>
  client.post("/admin/products/silhouette-batch/estimate", data).then((r) => r.data);
export const adminSilhouetteBatchStart = (data) =>
  client.post("/admin/products/silhouette-batch", data).then((r) => r.data);
export const adminSilhouetteBatchStatus = (jobId) =>
  client.get(`/admin/products/silhouette-batch/${jobId}`).then((r) => r.data);
export const adminSilhouetteBatchActive = () =>
  client.get("/admin/products/silhouette-batch/active").then((r) => r.data);
export const adminSilhouetteBatchStop = (jobId) =>
  client.post(`/admin/products/silhouette-batch/${jobId}/stop`).then((r) => r.data);
export const adminSilhouetteGenerateOne = (data) =>
  client.post("/admin/products/silhouette-generate-one", data).then((r) => r.data);
export const adminAiGenerateSilhouetteColor = (payload) =>
  startAiImageJob("/admin/products/ai-generate-silhouette-color-async", payload); // { path, url, color_name }
export const adminAiGenerateSilhouette3D = (payload) =>
  startAiImageJob("/admin/products/ai-generate-silhouette-3d-async", payload); // { path, url, n_colors }
export const adminAiGenerateSilhouette3DColor = (payload) =>
  startAiImageJob("/admin/products/ai-generate-silhouette-3d-color-async", payload); // { path, url, color_name, view }
export const adminMirrorSilhouette = (source) =>
  client.post("/admin/products/silhouette-mirror", { source }, { timeout: 30000 }).then((r) => r.data); // { path, url, mirrored }
// iter157 · Propone cornice + aree 2° colore leggendo il contorno (zero AI)
export const adminSilhouetteAnalyze = (payload) =>
  client.post("/admin/products/silhouette-analyze", payload, { timeout: 60000 }).then((r) => r.data); // { image_size, frame, color2, style, base_url }
export const adminAiUsage = (month = null) =>
  client.get("/admin/ai-usage", { params: month ? { month } : {} }).then((r) => r.data);
export const adminAiUsageEvents = (limit = 50) =>
  client.get("/admin/ai-usage/events", { params: { limit } }).then((r) => r.data.events);
export const adminAiSettings = () =>
  client.get("/admin/ai-settings").then((r) => r.data);
export const adminUpdateAiSettings = (monthly_credit_limit) =>
  client.put("/admin/ai-settings", { monthly_credit_limit }).then((r) => r.data);
export const adminBackfillKeywords = (payload = {}) =>
  client.post("/admin/products/backfill-keywords", payload, { timeout: 300000 }).then((r) => r.data);
export const adminImageModels = () =>
  client.get("/admin/ai-image-models").then((r) => r.data);
export const adminSaveImageModels = (quality, fast) =>
  client.put("/admin/ai-image-models", { quality, fast }).then((r) => r.data);
export const adminGeminiKeyCheck = () =>
  client.post("/admin/gemini-key-check").then((r) => r.data);

// --- Admin products -------------------------------------------------------
export const adminGetProducts = () => client.get("/admin/products").then((r) => r.data);
// Libreria dei testi dei 3 quadratini "In sintesi" già usati in catalogo.
export const adminProductTileLibrary = () => client.get("/admin/products/tile-library").then((r) => r.data);
// Clienti e numeri (insights admin) + conteggio visite pubblico.
export const adminInsights = (period = "month") =>
  client.get("/admin/insights", { params: { period } }).then((r) => r.data);
export const adminInsightsCsv = () =>
  client.get("/admin/insights/customers.csv", { responseType: "blob" }).then((r) => r.data);
export const adminSaveCustomerNote = (email, note) =>
  client.put("/admin/customers/note", { email, note }).then((r) => r.data);
export const trackVisit = (visitor, path) =>
  client.post("/track/visit", { visitor, path }).then((r) => r.data);
export const adminCreateProduct = (data) => client.post("/admin/products", data).then((r) => r.data);
export const adminUpdateProduct = (id, data) => client.patch(`/admin/products/${id}`, data).then((r) => r.data);
export const adminDeleteProduct = (id) => client.delete(`/admin/products/${id}`).then((r) => r.data);

// --- Admin categories -----------------------------------------------------
export const adminGetCategories = () => client.get("/admin/categories").then((r) => r.data);
export const adminCreateCategory = (data) => client.post("/admin/categories", data).then((r) => r.data);
export const adminUpdateCategory = (id, data) => client.patch(`/admin/categories/${id}`, data).then((r) => r.data);
export const adminDeleteCategory = (id) => client.delete(`/admin/categories/${id}`).then((r) => r.data);

// --- Admin quotes ---------------------------------------------------------
export const adminGetQuotes = () => client.get("/admin/quotes").then((r) => r.data);
// iter362 · Agenda chiamate: segno «chiamata fatta» salvato sul preventivo.
export const adminMarkCallDone = (id, done) => client.post(`/admin/quotes/${id}/call-done`, { done }).then((r) => r.data);
export const designerMarkCallDone = (designerId, quoteId, done) => client.post(`/designer/${designerId}/quotes/${quoteId}/call-done`, { done }).then((r) => r.data);
// Triage Jev delle richieste: tipo, urgenza, valore, spam, priorità 0-100.
export const adminTriageQuote = (id) =>
  client.post(`/admin/quotes/${id}/triage`).then((r) => r.data); // { ok, triage }
export const adminTriageMissing = (limit = 20) =>
  client.post("/admin/quotes/triage-missing", null, { params: { limit } }).then((r) => r.data); // { checked, classified }
export const adminUpdateQuoteStatus = (id, status) =>
  client.patch(`/admin/quotes/${id}/status`, { status }).then((r) => r.data);

// --- Admin size-guide presets ---------------------------------------------
export const adminGetSizePresets = () => client.get("/admin/size-presets").then((r) => r.data);
export const adminSaveSizePreset = (data) => client.post("/admin/size-presets", data).then((r) => r.data);
export const adminDeleteSizePreset = (id) => client.delete(`/admin/size-presets/${id}`).then((r) => r.data);

// --- Admin MASTER size charts (per category) + auto-generation ------------
export const adminGetMasterSizeCharts = () => client.get("/admin/master-size-charts").then((r) => r.data);
export const adminSaveMasterSizeCharts = (charts) => client.put("/admin/master-size-charts", { charts }).then((r) => r.data);
export const adminPreviewSizeGuide = (form) => client.post("/admin/products/preview-size-guide", form).then((r) => r.data);
export const adminGenerateAllSizeGuides = (overwrite = true) => client.post("/admin/products/generate-size-guides", { overwrite }).then((r) => r.data);
export const adminAiEstimateSizes = (form) => client.post("/admin/products/ai-estimate-sizes", form).then((r) => r.data);
export const adminAiEstimateSizesBulk = (opts = {}) => client.post("/admin/products/ai-estimate-sizes-bulk", opts).then((r) => r.data);
export const adminSizeEstimateJob = () => client.get("/admin/products/size-estimate-job").then((r) => r.data);

// --- Admin customizer config ---------------------------------------------
export const adminGetCustomizerConfig = () => client.get("/admin/customizer/config").then((r) => r.data);
export const adminUpdateCustomizerConfig = (data) => client.put("/admin/customizer/config", data).then((r) => r.data);
export const adminResetCustomizerConfig = () => client.post("/admin/customizer/reset").then((r) => r.data);

// --- Admin placement layouts (per-category print position overrides) -----
export const adminListPlacementLayouts = () => client.get("/admin/placement-layouts").then((r) => r.data);
export const adminGetPlacementLayout = (category) => client.get(`/admin/placement-layouts/${category}`).then((r) => r.data);
export const adminSavePlacementLayout = (category, positions, dimension_offsets = {}, disabled_positions = []) => client.put(`/admin/placement-layouts/${category}`, { positions, dimension_offsets, disabled_positions }).then((r) => r.data);
export const adminResetPlacementLayout = (category) => client.delete(`/admin/placement-layouts/${category}`).then((r) => r.data);
export const adminListPlacementPresets = (category) => client.get(`/admin/placement-layout-presets${category ? `?category=${encodeURIComponent(category)}` : ""}`).then((r) => r.data);
export const adminCreatePlacementPreset = (payload) => client.post("/admin/placement-layout-presets", payload).then((r) => r.data);
export const adminDeletePlacementPreset = (id) => client.delete(`/admin/placement-layout-presets/${id}`).then((r) => r.data);

// --- Squiddi customizer helper (position/size + outfit) -------------------
export const squiddiSuggest = (payload) => client.post("/squiddi/suggest", payload).then((r) => r.data);

// --- Image library (customer-private + admin presets) ---------------------
export const libraryMine = () => client.get("/library/mine").then((r) => r.data);
export const libraryUpload = (file, name) => {
  const fd = new FormData();
  fd.append("file", file);
  if (name) fd.append("name", name);
  return client.post("/library/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const libraryDelete = (id) => client.delete(`/library/${id}`).then((r) => r.data);
export const libraryPresets = () => client.get("/library/presets").then((r) => r.data);
// Personalizzatore guidato · stili di testo, modelli e Squiddi AI.
export const getLabPresets = (kind) => client.get("/lab-presets", { params: kind ? { kind } : {} }).then((r) => r.data);
export const adminLabPresets = () => client.get("/admin/lab-presets").then((r) => r.data);
export const adminPatchLabPreset = (id, patch) => client.patch(`/admin/lab-presets/${id}`, patch).then((r) => r.data);
export const adminDeleteLabPreset = (id) => client.delete(`/admin/lab-presets/${id}`).then((r) => r.data);
export const adminReorderLabPresets = (kind, ids) => client.post("/admin/lab-presets/reorder", { kind, ids }).then((r) => r.data);
export const askLabAssistant = (body) => client.post("/customizer/assistant", body).then((r) => r.data);
export const adminLibraryPresets = () => client.get("/admin/library/presets").then((r) => r.data);
export const adminLibraryUploadPreset = (file, { name, category } = {}) => {
  const fd = new FormData();
  fd.append("file", file);
  if (name) fd.append("name", name);
  if (category) fd.append("category", category);
  return client.post("/admin/library/presets", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const adminLibraryDeletePreset = (id) => client.delete(`/admin/library/presets/${id}`).then((r) => r.data);
export const adminLibraryUpdatePreset = (id, patch) => client.patch(`/admin/library/presets/${id}`, patch).then((r) => r.data);
export const adminLibrarySeedDefaults = (force = false) => client.post(`/admin/library/presets/seed-defaults${force ? "?force=true" : ""}`).then((r) => r.data);

// --- Bookings (chiamate con un esperto) ------------------------------------
export const listBookingTypes = () => client.get("/bookings/types").then((r) => r.data);
export const getBookingAvailability = (date, callType) =>
  client.get("/bookings/availability", { params: { date, call_type: callType } }).then((r) => r.data);
export const createBooking = (payload) => client.post("/bookings", payload).then((r) => r.data);
export const listMyBookings = () => client.get("/me/bookings").then((r) => r.data);
export const cancelMyBooking = (id) => client.delete(`/me/bookings/${id}`).then((r) => r.data);
export const adminListBookings = (params = {}) => client.get("/admin/bookings", { params }).then((r) => r.data);
export const adminBookingUnreadCount = () => client.get("/admin/bookings/unread-count").then((r) => r.data);
export const adminUpdateBooking = (id, patch) => client.patch(`/admin/bookings/${id}`, patch).then((r) => r.data);
export const adminDeleteBooking = (id) => client.delete(`/admin/bookings/${id}`).then((r) => r.data);

export default client;
